import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {
    CertificationPolicyConditionRequest,
    PreviewScopeRequest,
    PreviewScopeResponse,
} from '@/types/certification'
import {ScopeMode, type ScopeMode as ScopeModeType} from '@/features/certification/scope-mode'

const DEBOUNCE_MS = 350

export interface ScopeConditionEntry {
    field: string
    operator: string
    value: string
}

export interface UseCertificationScopePreviewInput {
    scopeMode: ScopeModeType
    conditions: ScopeConditionEntry[]
    conditionExpr: string
}

export interface UseCertificationScopePreviewResult {
    data: PreviewScopeResponse | undefined
    error: Error | null
    /** True while a debounce window OR a request is in flight (gates Next). */
    inFlight: boolean
    /** True only when the panel has no query to show (no-query state). */
    idle: boolean
    retry: () => void
}

/** Whether the given inputs should fire a preview request at all. */
function shouldQuery(input: UseCertificationScopePreviewInput): boolean {
    if (input.scopeMode === ScopeMode.EXPRESSION) {
        return input.conditionExpr.trim().length > 0
    }
    return true
}

function buildBody(input: UseCertificationScopePreviewInput): PreviewScopeRequest {
    if (input.scopeMode === ScopeMode.EXPRESSION) {
        return {conditionExpr: input.conditionExpr.trim()}
    }
    if (input.conditions.length === 0) {
        return {}
    }
    const conditions: CertificationPolicyConditionRequest[] = input.conditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
    }))
    return {conditions}
}

function bodyKey(body: PreviewScopeRequest): string {
    return JSON.stringify(body)
}

/**
 * Debounced wrapper around `services.certificationPolicies.previewScope`. Builds
 * the request body (`conditions[]` XOR `conditionExpr`; both empty → empty body =
 * "certify everything"), does not fire when expression mode has a blank/whitespace
 * expression, and exposes an `inFlight` flag that covers BOTH the debounce window
 * and the request itself (the wizard reads it to disable "Next" — spec §5.2/§7.3).
 */
export function useCertificationScopePreview(
    input: UseCertificationScopePreviewInput,
): UseCertificationScopePreviewResult {
    const services = useServices()
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [debouncing, setDebouncing] = useState(false)

    const mutation = useMutation({
        mutationFn: (body: PreviewScopeRequest) => services.certificationPolicies.previewScope(body),
        // WHY: a live preview validates incomplete expressions on every keystroke;
        // its expected 400s are shown inline by WizardStepScope, so suppress the
        // global error toast + error_logs mirror (see createMutationErrorHandler).
        meta: {suppressGlobalError: true},
    })
    const mutate = mutation.mutate
    const reset = mutation.reset

    const active = shouldQuery(input)
    const body = useMemo(() => buildBody(input), [input])
    const key = active ? bodyKey(body) : null

    // WHY: hold the latest body in a ref so the debounce effect can depend on the
    // stable serialized `key` only. Callers pass a fresh `input` object on every
    // render — keying the effect on the object identity would re-fire the debounce
    // every render and never settle.
    const bodyRef = useRef(body)
    bodyRef.current = body

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    useEffect(() => {
        clearTimer()
        if (key === null) {
            setDebouncing(false)
            reset()
            return
        }
        setDebouncing(true)
        timerRef.current = setTimeout(() => {
            timerRef.current = null
            setDebouncing(false)
            mutate(bodyRef.current)
        }, DEBOUNCE_MS)
        return clearTimer
    }, [key, mutate, reset, clearTimer])

    const retry = useCallback(() => {
        if (!active) return
        clearTimer()
        setDebouncing(false)
        mutate(bodyRef.current)
    }, [active, mutate, clearTimer])

    return {
        data: active ? mutation.data : undefined,
        error: active ? (mutation.error as Error | null) : null,
        inFlight: active && (debouncing || mutation.isPending),
        idle: !active,
        retry,
    }
}
